import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchPurchases } from '@/services/purchases';
import { fetchSuppliers } from '@/services/suppliers';
import { queryKeys } from '@/lib/queryKeys';
import { parseDatabaseError } from '@/lib/errors';
import { Purchase } from '@/types/models';
import { Modal } from '@/components/common/Modal';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { Pagination } from '@/components/common/Pagination';
import { SearchInput } from '@/components/common/SearchInput';
import { ReceivePurchaseForm, ReceivePurchasePrefill } from '@/features/purchases/ReceivePurchaseForm';
import { PurchaseDetail } from '@/features/purchases/PurchaseDetail';
import { InvoiceExtractorModal, InvoiceExtractPreFill } from '@/features/ai/InvoiceExtractorModal';
import { useAuth } from '@/context/AuthContext';
import {
  ShoppingBag,
  Plus,
  ChevronDown,
  CalendarDays,
  Building2,
  Loader2,
  Sparkles,
} from 'lucide-react';

const PAGE_SIZE = 25;

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-PK', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatCurrency(val: number | null | undefined): string {
  if (val == null) return '—';
  return `Rs. ${val.toLocaleString('en-PK')}`;
}

function PaymentStatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    paid: 'bg-green-100 text-green-800',
    partial: 'bg-amber-100 text-amber-800',
    pending: 'bg-red-100 text-red-800',
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide ${
        styles[status] ?? 'bg-brand-bg text-brand-muted'
      }`}
    >
      {status}
    </span>
  );
}

export const PurchasesPage: React.FC = () => {
  const { role } = useAuth();
  const isOwner = role === 'owner';
  const [search, setSearch] = useState('');
  const [supplierId, setSupplierId] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [archiveFilter, setArchiveFilter] = useState<'active' | 'archived' | 'all'>('active');
  const [page, setPage] = useState(0);

  const [receiveModalOpen, setReceiveModalOpen] = useState(false);
  const [aiExtractModalOpen, setAiExtractModalOpen] = useState(false);
  const [aiPreFill, setAiPreFill] = useState<ReceivePurchasePrefill | undefined>(undefined);
  const [detailPurchaseId, setDetailPurchaseId] = useState<string | null>(null);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: queryKeys.purchases.list({ search, supplierId, status: statusFilter, archiveFilter, page }),
    queryFn: () => fetchPurchases({ search, supplierId, status: statusFilter, archiveFilter, page }),
    placeholderData: (prev) => prev,
  });

  const { data: suppliersData } = useQuery({
    queryKey: queryKeys.suppliers.list({ page: 0 }),
    queryFn: () => fetchSuppliers({ page: 0 }),
  });

  const purchases = data?.data ?? [];
  const totalCount = data?.count ?? 0;

  const handleSearch = (val: string) => {
    setSearch(val);
    setPage(0);
  };

  const handleReceiveSuccess = () => {
    setReceiveModalOpen(false);
    setAiPreFill(undefined);
    void refetch();
  };

  const handleAiConfirmed = (preFill: InvoiceExtractPreFill) => {
    setAiExtractModalOpen(false);
    setAiPreFill(preFill);
    setReceiveModalOpen(true);
  };

  return (
    <div className="space-y-5">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-lg font-bold text-brand-black">Stock Purchases</h1>
          <p className="text-xs text-brand-muted mt-0.5">
            {isLoading ? 'Loading…' : `${totalCount.toLocaleString('en-PK')} purchase${totalCount !== 1 ? 's' : ''}`}
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setAiExtractModalOpen(true)}
            className="inline-flex items-center space-x-2 px-3.5 py-2 bg-white border border-brand-green text-brand-green hover:bg-green-50 text-sm font-semibold rounded-md transition-colors shadow-sm"
          >
            <Sparkles className="w-4 h-4 text-brand-green" />
            <span>Extract Invoice (AI)</span>
          </button>
          <button
            onClick={() => {
              setAiPreFill(undefined);
              setReceiveModalOpen(true);
            }}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-brand-green text-white text-sm font-semibold rounded-md hover:bg-brand-green-hover transition-colors shadow-sm"
          >
            <Plus className="w-4 h-4" />
            <span>Receive Purchase</span>
          </button>
        </div>
      </div>

      {/* Filters Row */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="flex-1">
          <SearchInput
            value={search}
            onChange={handleSearch}
            placeholder="Search invoice ref or notes…"
          />
        </div>

        {isOwner && (
          <div className="relative">
            <select
              value={archiveFilter}
              onChange={(e) => { setArchiveFilter(e.target.value as 'active' | 'archived' | 'all'); setPage(0); }}
              className="h-9 pl-3 pr-8 bg-slate-50 border border-slate-300 rounded-md text-xs font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-500 appearance-none"
            >
              <option value="active">Active Only</option>
              <option value="archived">Archived</option>
              <option value="all">All Records</option>
            </select>
            <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
          </div>
        )}

        <div className="relative">
          <select
            value={supplierId}
            onChange={(e) => { setSupplierId(e.target.value); setPage(0); }}
            className="h-9 pl-3 pr-8 bg-white border border-brand-border rounded-md text-xs text-brand-black focus:outline-none focus:ring-2 focus:ring-brand-green appearance-none"
          >
            <option value="">All Suppliers</option>
            {suppliersData?.data.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
          <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-brand-muted" />
        </div>

        <div className="relative">
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
            className="h-9 pl-3 pr-8 bg-white border border-brand-border rounded-md text-xs text-brand-black focus:outline-none focus:ring-2 focus:ring-brand-green appearance-none"
          >
            <option value="">All Statuses</option>
            <option value="paid">Paid</option>
            <option value="partial">Partial</option>
            <option value="pending">Unpaid</option>
          </select>
          <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-brand-muted" />
        </div>
      </div>

      {/* Error State */}
      {isError && (
        <ErrorAlert message={parseDatabaseError(error)} onRetry={() => refetch()} />
      )}

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-brand-green animate-spin" />
        </div>
      )}

      {/* Table */}
      {!isLoading && !isError && (
        <>
          {purchases.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <ShoppingBag className="w-12 h-12 text-brand-muted/30 mb-3" />
              <p className="text-sm font-semibold text-brand-black">No purchases found</p>
              <p className="text-xs text-brand-muted mt-1">
                {search || supplierId || statusFilter
                  ? 'Try adjusting your search or filters.'
                  : 'Receive your first purchase using the button above.'}
              </p>
            </div>
          ) : (
            <div className="bg-white border border-brand-border rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-brand-bg border-b border-brand-border text-brand-muted font-semibold uppercase tracking-wide">
                    <tr>
                      <th className="text-left px-4 py-3">Date</th>
                      <th className="text-left px-4 py-3">Invoice / Ref</th>
                      <th className="text-left px-4 py-3">Supplier</th>
                      <th className="text-left px-4 py-3">Items</th>
                      <th className="text-right px-4 py-3">Total Amount</th>
                      <th className="text-right px-4 py-3">Amount Paid</th>
                      <th className="text-right px-4 py-3">Balance Due</th>
                      <th className="text-left px-4 py-3">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-brand-border">
                    {purchases.map((purchase: Purchase) => {
                      const balanceDue = Math.max(0, (purchase.total || 0) - (purchase.amount_paid || 0));
                      return (
                        <tr
                          key={purchase.id}
                          onClick={() => setDetailPurchaseId(purchase.id)}
                          className={`hover:bg-brand-bg/60 cursor-pointer transition-colors ${
                            purchase.archived_at ? 'bg-slate-50/60 opacity-85' : ''
                          }`}
                        >
                          <td className="px-4 py-3 text-brand-muted whitespace-nowrap">
                            <span className="flex items-center space-x-1.5">
                              <CalendarDays className="w-3.5 h-3.5 shrink-0" />
                              <span>{formatDate(purchase.purchase_date)}</span>
                            </span>
                          </td>
                          <td className="px-4 py-3 font-medium text-brand-black">
                            <div className="flex items-center space-x-1.5 flex-wrap gap-y-1">
                              <span>{purchase.supplier_invoice_number || purchase.purchase_number || (
                                <span className="text-brand-muted italic">No ref</span>
                              )}</span>
                              {purchase.archived_at && (
                                <span className="px-1.5 py-0.5 bg-slate-200 text-slate-700 text-[10px] font-bold rounded uppercase">
                                  Archived
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-brand-muted">
                            <span className="flex items-center space-x-1.5">
                              <Building2 className="w-3.5 h-3.5 shrink-0" />
                              <span>{purchase.supplier?.name ?? '—'}</span>
                            </span>
                          </td>
                          <td className="px-4 py-3 text-brand-muted">
                            {purchase.items ? `${purchase.items.length} item${purchase.items.length !== 1 ? 's' : ''}` : '—'}
                          </td>
                          <td className="px-4 py-3 text-right font-bold text-brand-black">
                            {formatCurrency(purchase.total)}
                          </td>
                          <td className="px-4 py-3 text-right text-brand-green font-semibold">
                            {formatCurrency(purchase.amount_paid)}
                          </td>
                          <td className={`px-4 py-3 text-right font-semibold ${balanceDue > 0 ? 'text-brand-red' : 'text-brand-muted'}`}>
                            {balanceDue > 0 ? formatCurrency(balanceDue) : '—'}
                          </td>
                          <td className="px-4 py-3">
                            <PaymentStatusBadge status={purchase.payment_status} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Pagination */}
          {totalCount > PAGE_SIZE && (
            <Pagination page={page} totalCount={totalCount} onPageChange={setPage} />
          )}
        </>
      )}

      {/* AI Invoice Extractor Modal */}
      <Modal
        isOpen={aiExtractModalOpen}
        onClose={() => setAiExtractModalOpen(false)}
        title="Extract Supplier Invoice with AI"
        maxWidth="4xl"
      >
        <InvoiceExtractorModal
          onConfirm={handleAiConfirmed}
          onCancel={() => setAiExtractModalOpen(false)}
        />
      </Modal>

      {/* Receive Purchase Modal */}
      <Modal
        isOpen={receiveModalOpen}
        onClose={() => {
          setReceiveModalOpen(false);
          setAiPreFill(undefined);
        }}
        title="Receive New Purchase"
        maxWidth="lg"
      >
        <ReceivePurchaseForm
          initialData={aiPreFill}
          onSuccess={handleReceiveSuccess}
          onCancel={() => {
            setReceiveModalOpen(false);
            setAiPreFill(undefined);
          }}
        />
      </Modal>

      {/* Purchase Detail Modal */}
      <Modal
        isOpen={!!detailPurchaseId}
        onClose={() => setDetailPurchaseId(null)}
        title="Purchase Details"
        maxWidth="lg"
      >
        {detailPurchaseId && (
          <PurchaseDetail
            purchaseId={detailPurchaseId}
            onClose={() => setDetailPurchaseId(null)}
          />
        )}
      </Modal>
    </div>
  );
};
